import React from 'react';
import {
  StyledCard,
  StyledCardContent,
  StyledCardHeader,
} from './InformationCard.styles';
import { CardList } from './components/CardList';
import { Typography } from '../typography';
import { InformationCardProps, ItemAction } from './InformationCard.types';
import { IconButton } from '../icon-button';
import { CardGroup } from './components/CardGroup';
import { CardStatus } from './components/CardStatus';
import { CardTable } from './components/CardTable';
import { ShimmerComponents } from './components/Shimmers';
import { SxProps, Theme } from '@mui/material';

/**
 * ContentComponent object for mapping the different types of content components.
 * Maps the `as` prop to the corresponding rendering component, enabling dynamic content rendering.
 *
 * @category InformationCard
 * @constant
 */
const ContentComponent = {
  list: CardList,
  status: CardStatus,
  table: CardTable,
};

/**
 * InformationCard component for displaying various types of information in a card format.
 *
 * This component dynamically renders content based on the `as` prop and supports customization
 * with optional headers, styles, and actions.
 *
 * @param {InformationCardProps} props - The properties for the InformationCard component.
 * @returns {React.ReactElement} The rendered InformationCard component.
 * @category InformationCard
 * @document InformationCardUsage.md
 *
 * @example
 * ```tsx
 * <InformationCard
 *   as="list"
 *   title="Example Card"
 *   subheader="This is a subheader"
 *   data={{
 *     items: [
 *       { primary: 'Item 1', secondary: 'Details about item 1' },
 *       { primary: 'Item 2', secondary: 'Details about item 2' },
 *     ],
 *   }}
 * />
 * ```
 */
export const InformationCard: React.FC<InformationCardProps> = (
  props,
): React.ReactElement => {
  const cardRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const cardElement = cardRef.current;
    if (cardElement) {
      if (cardElement.scrollHeight > cardElement.clientHeight) {
        cardElement.classList.add('has-overflow');
      } else {
        cardElement.classList.remove('has-overflow');
      }
    }
  }, [cardRef, props.data]);

  let DynamicComponent =
    ContentComponent[props.as as keyof typeof ContentComponent];

  let displayHeader = true;
  if (props.consolidatedData) {
    DynamicComponent = CardGroup;
    displayHeader = false;
  }

  if (props.HeaderComponent) {
    displayHeader = false;
  }

  const Children = React.useMemo(() => {
    if (props.loading) {
      const Shimmer =
        ShimmerComponents[props.as as keyof typeof ShimmerComponents];
      return <Shimmer />;
    }
    return DynamicComponent ? <DynamicComponent {...props} /> : props.children;
  }, [props, DynamicComponent]);

  return (
    <StyledCard fullHeight={props.fullHeight} sx={props.style} ref={cardRef}>
      {displayHeader && props.title && (
        <CardHeader
          title={props.title}
          subheader={props.subheader ?? ''}
          action={props.cardAction}
        />
      )}
      {props.HeaderComponent && (
        <StyledCardHeader>{props.HeaderComponent}</StyledCardHeader>
      )}
      <CardContent cardContentSx={props.childrenCardContentSx}>
        {Children}
      </CardContent>
    </StyledCard>
  );
};

/**
 * CardHeader component for rendering the header of the card.
 *
 * This component handles the display of the card title, subheader, and optional actions.
 *
 * @param {object} props - The properties for the CardHeader component.
 * @param {string} props.title - The title to display in the card header.
 * @param {string} [props.subheader] - The subheader to display below the title.
 * @param {ItemAction} [props.action] - The action to display as a button in the header.
 * @returns {React.ReactElement} The rendered CardHeader component.
 * @category InformationCard
 *
 * @example
 * ```tsx
 * <CardHeader
 *   title="Card Title"
 *   subheader="Optional Subheader"
 *   action={{ action: () => alert('Clicked!'), icon: EditIcon }}
 * />
 * ```
 */
const CardHeader = (props: {
  title: string;
  subheader?: string;
  action?: ItemAction;
}): React.ReactElement => {
  const Wrapper = props.action?.action ? HeaderAction : React.Fragment;
  const wrapperProps = props.action?.action ? { action: props.action } : {};

  return (
    <StyledCardHeader>
      <div>
        <Typography fontWeight="medium">{props.title}</Typography>
        {props.subheader && (
          <Typography fontSize={12}>{props.subheader}</Typography>
        )}
      </div>
      {props.action && (
        <Wrapper {...(wrapperProps as HeaderActionProps)}>
          <props.action.icon
            fontSize="inherit"
            sx={{ width: 28, height: 32 }}
          />
        </Wrapper>
      )}
    </StyledCardHeader>
  );
};

/**
 * Properties for the HeaderAction component.
 *
 * @category InformationCard
 * @property {ItemAction} action - The action to be executed when the button is clicked.
 * @property {React.ReactNode} children - The children of the HeaderAction component.
 */
export type HeaderActionProps = {
  /** The action to be executed when the button is clicked. */
  action: ItemAction;

  /** The children of the HeaderAction component. */
  children: React.ReactNode;
};

/**
 * HeaderAction component for rendering an action button in the card header.
 *
 * @param {HeaderActionProps} props - The properties for the HeaderAction component.
 * @returns {React.ReactElement} The rendered HeaderAction component.
 * @category InformationCard
 *
 * @example
 * ```tsx
 * <HeaderAction action={{ action: () => console.log('Action clicked!') }}>
 *   <DeleteIcon />
 * </HeaderAction>
 * ```
 */
export const HeaderAction = (props: HeaderActionProps): React.ReactElement => {
  return (
    <IconButton
      aria-label="delete"
      onClick={() =>
        props.action?.action?.({ primary: '', id: props.action.action.name })
      }
    >
      {props.children}
    </IconButton>
  );
};

/**
 * CardContent component for rendering the content of the card.
 *
 * This component wraps the card's children and applies consistent styling.
 *
 * @category InformationCard
 * @param {object} props - The properties for the CardContent component.
 * @param {React.ReactNode} props.children - The content to render inside the card.
 * @param {SxProps<Theme>} props.cardContentSx - The custom sx to be consumed by the Card.
 * @returns {React.ReactElement} The rendered CardContent component.
 *
 * @example
 * ```tsx
 * <CardContent>
 *   <Typography variant="body2" color="textSecondary" component="p">
 *     This is the content of the card.
 *   </Typography>
 * </CardContent>
 * ```
 */
const CardContent = (props: {
  children: React.ReactNode;
  cardContentSx?: SxProps<Theme>;
}): React.ReactElement => {
  return (
    <StyledCardContent sx={props.cardContentSx}>
      {props.children}
    </StyledCardContent>
  );
};
